import React, { useEffect, useMemo, useState } from "react";
import {
  IconBook2,
  IconCircleCheck,
  IconChevronDown,
  IconHelpCircle,
  IconPlus,
  IconSearch,
  IconX,
} from "@tabler/icons-react";
import {
  useEncoderAhaByIds,
  useEncoderAhaSearch,
  useEncoderAhaYears,
  useEncoderIcdChapters,
  useEncoderIcdGuidelineSearch,
  useEncoderIcdGuidelineYears,
  useEncoderIcdGuidelinesByIds,
  useEncoderIcdSubChapters,
  useEncoderIcdSubTopics,
  useEncoderIcdTopics,
} from "../../hooks/queries";
import type { AhaGuideline, EncoderGuidelineType, IcdGuideline } from "../../types/encoder";
import {
  AHA_QUARTER_CHIPS,
  cleanAhaMultilineText,
  cn,
  formatAhaGuidelineTitle,
  formatAhaYearQuarterPage,
  formatIcdGuidelineHeader,
  getAhaExpandedQaContent,
  mapAhaQuarterToDb,
  sanitizeGuidelineText,
} from "../../utils/encoder";
import { Spinner } from "./shared";

const FilterSelect: React.FC<{
  value: string;
  onChange: (value: string) => void;
  options: string[];
  placeholder: string;
  disabled?: boolean;
  isLoading?: boolean;
  emptyMessage?: string;
}> = ({
  value,
  onChange,
  options,
  placeholder,
  disabled,
  isLoading,
  emptyMessage = "No options available",
}) => {
  const isEmpty = !isLoading && options.length === 0;
  return (
    <div>
      <select
        value={value}
        disabled={disabled || isLoading || isEmpty}
        onChange={(event) => onChange(event.target.value)}
        className="h-10 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm outline-none disabled:bg-gray-50"
      >
        <option value="">
          {isLoading ? "Loading..." : isEmpty ? emptyMessage : placeholder}
        </option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
      {!disabled && isEmpty ? (
        <p className="mt-1 text-xs text-gray-500">{emptyMessage}</p>
      ) : null}
    </div>
  );
};

const NoteCard: React.FC<{
  title: string;
  tone: "blue" | "red" | "green" | "white";
  icon?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, tone, icon, children }) => {
  const tones = {
    blue: {
      wrap: "border-blue-200 bg-blue-50/70",
      head: "border-blue-200 bg-blue-100",
    },
    red: {
      wrap: "border-red-200",
      head: "border-red-200 bg-red-100",
    },
    green: {
      wrap: "border-green-200",
      head: "border-green-200 bg-green-100",
    },
    white: {
      wrap: "border-slate-200 bg-white",
      head: "",
    },
  }[tone];
  const bodyClass =
    tone === "red"
      ? "bg-red-50 px-3 py-2.5 text-gray-800"
      : tone === "green"
        ? "bg-green-50 px-3 py-2.5 text-gray-800"
        : tone === "blue"
          ? "whitespace-pre-wrap px-3 py-2.5 text-slate-700"
          : "whitespace-pre-line px-3 py-3 text-gray-800";

  return (
    <div className={cn("overflow-hidden rounded-lg border", tones.wrap)}>
      {title ? (
        <div
          className={cn(
            "flex items-center gap-2 border-b px-3 py-2",
            tones.head
          )}
        >
          {icon}
          <p className="text-xs font-bold uppercase tracking-wide text-black">
            {title}
          </p>
        </div>
      ) : null}
      <div className={bodyClass}>{children}</div>
    </div>
  );
};

const ModeToggle: React.FC<{
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (value: string) => void;
}> = ({ value, options, onChange }) => (
  <div className="flex w-full items-center rounded-md border border-gray-300 bg-gray-100 p-1">
    {options.map((option) => (
      <button
        key={option.value}
        type="button"
        onClick={() => onChange(option.value)}
        className={cn(
          "flex-1 rounded px-4 py-1.5 text-sm font-semibold transition-all",
          value === option.value
            ? "bg-blue-600 text-white shadow-sm"
            : "text-gray-600 hover:bg-white/70 hover:text-gray-900"
        )}
      >
        {option.label}
      </button>
    ))}
  </div>
);

const CodingGuideTab: React.FC = () => {
  const [mode, setMode] = useState<"icd" | "aha">("icd");
  const [guidelineType, setGuidelineType] =
    useState<EncoderGuidelineType>("ICD-10-CM");
  const [keyword, setKeyword] = useState("");
  const [year, setYear] = useState("");
  const [chapter, setChapter] = useState("");
  const [subChapter, setSubChapter] = useState("");
  const [topic, setTopic] = useState("");
  const [subTopic, setSubTopic] = useState("");
  const [expandedIcd, setExpandedIcd] = useState<number[]>([]);
  const [ahaKeyword, setAhaKeyword] = useState("");
  const [ahaFilter, setAhaFilter] = useState<"keyword" | "year">("keyword");
  const [ahaYear, setAhaYear] = useState("");
  const [ahaQuarters, setAhaQuarters] = useState<string[]>([]);
  const [expandedAha, setExpandedAha] = useState<number[]>([]);

  const icdYears = useEncoderIcdGuidelineYears(guidelineType);
  const chapters = useEncoderIcdChapters(guidelineType);
  const subChapters = useEncoderIcdSubChapters(guidelineType, chapter);
  const topics = useEncoderIcdTopics(guidelineType, subChapter);
  const subTopics = useEncoderIcdSubTopics(guidelineType, topic);
  const icdSearch = useEncoderIcdGuidelineSearch({
    guidelineType,
    keyword: keyword.trim() || undefined,
    year: year || undefined,
    chapter: chapter || undefined,
    subChapter: subChapter || undefined,
    topic: topic || undefined,
    subTopic: subTopic || undefined,
  });
  const icdBody = useEncoderIcdGuidelinesByIds(expandedIcd, expandedIcd.length > 0);

  const ahaYears = useEncoderAhaYears();
  const ahaSearch = useEncoderAhaSearch({
    keyword: ahaFilter === "keyword" ? ahaKeyword.trim() || undefined : undefined,
    year: ahaFilter === "year" ? ahaYear || undefined : undefined,
    quarters:
      ahaFilter === "year" && ahaQuarters.length
        ? ahaQuarters.map(mapAhaQuarterToDb)
        : undefined,
  });
  const ahaBody = useEncoderAhaByIds(expandedAha, expandedAha.length > 0);

  useEffect(() => {
    setChapter("");
    setSubChapter("");
    setTopic("");
    setSubTopic("");
    setExpandedIcd([]);
  }, [guidelineType]);

  const icdRows: IcdGuideline[] = icdSearch.data ?? [];
  const ahaRows = useMemo(() => {
    const rows: AhaGuideline[] = ahaSearch.data ?? [];
    return [...rows].sort((a: AhaGuideline, b: AhaGuideline) => {
      const yearDiff = (b.fyear ?? 0) - (a.fyear ?? 0);
      if (yearDiff) return yearDiff;
      return (b.id ?? 0) - (a.id ?? 0);
    });
  }, [ahaSearch.data]);

  const allIcdExpanded = icdRows.length > 0 && expandedIcd.length === icdRows.length;
  const allAhaExpanded = ahaRows.length > 0 && expandedAha.length === ahaRows.length;

  const resetAll = () => {
    setMode("icd");
    setGuidelineType("ICD-10-CM");
    setKeyword("");
    setYear("");
    setChapter("");
    setSubChapter("");
    setTopic("");
    setSubTopic("");
    setExpandedIcd([]);
    setAhaKeyword("");
    setAhaFilter("keyword");
    setAhaYear("");
    setAhaQuarters([]);
    setExpandedAha([]);
  };

  const toggleIcd = (id: number) => {
    setExpandedIcd((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
    );
  };

  const toggleAha = (id: number) => {
    setExpandedAha((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
    );
  };

  return (
    <div className="flex h-full min-h-0 flex-col bg-[#f9fafb] font-sans text-gray-900">
      <header className="grid shrink-0 grid-cols-[1fr_auto_1fr] items-center gap-4 border-b border-gray-200 bg-white px-6 py-2">
        <h1 className="inline-flex items-center gap-2 text-lg font-bold text-gray-900">
          <IconBook2 size={20} className="text-purple-700" />
          Coding Guide
        </h1>
        <div className="flex items-center justify-center rounded-md border border-gray-300 bg-gray-100 p-1">
          <button
            type="button"
            onClick={() => setMode("icd")}
            className={cn(
              "rounded px-4 py-1.5 text-sm font-semibold",
              mode === "icd"
                ? "bg-blue-600 text-white shadow-sm"
                : "text-gray-600 hover:bg-white/70 hover:text-gray-900"
            )}
          >
            ICD-10 Guidelines
          </button>
          <button
            type="button"
            onClick={() => setMode("aha")}
            className={cn(
              "rounded px-4 py-1.5 text-sm font-semibold",
              mode === "aha"
                ? "bg-blue-600 text-white shadow-sm"
                : "text-gray-600 hover:bg-white/70 hover:text-gray-900"
            )}
          >
            AHA Coding Clinic References
          </button>
        </div>
        <div className="flex items-center justify-end">
          <button
            type="button"
            onClick={resetAll}
            className="h-8 rounded-md border border-gray-300 px-3 text-sm font-semibold text-gray-700 hover:bg-gray-50"
          >
            Reset
          </button>
        </div>
      </header>

      <div className="relative min-h-0 flex-1 overflow-hidden">
        {mode === "icd" ? (
          <div className="absolute inset-0 flex flex-col gap-1.5 px-1 pt-1 pb-2 lg:flex-row">
            <div className="flex h-full w-full flex-col overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-sm lg:w-[30%]">
              <div className="border-b border-gray-200 bg-gray-50/50 p-3">
                <ModeToggle
                  value={guidelineType}
                  onChange={(value) =>
                    setGuidelineType(value as EncoderGuidelineType)
                  }
                  options={[
                    { value: "ICD-10-CM", label: "ICD-10-CM" },
                    { value: "ICD-10-PCS", label: "ICD-10-PCS" },
                  ]}
                />
                <div className="mt-3 space-y-3">
                  <div className="flex gap-3">
                    <div className="w-1/4">
                      <label className="mb-1.5 block text-sm font-semibold text-gray-900">
                        Year
                      </label>
                      <FilterSelect
                        value={year}
                        onChange={setYear}
                        placeholder="All"
                        isLoading={icdYears.isLoading}
                        options={(icdYears.data ?? []).map(String)}
                      />
                    </div>
                    <div className="w-3/4">
                      <label className="mb-1.5 block text-sm font-semibold text-gray-900">
                        Keyword Search
                      </label>
                      <div className="relative">
                        <IconSearch
                          size={16}
                          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                        />
                        <input
                          value={keyword}
                          onChange={(event) => {
                            setKeyword(event.target.value);
                            setChapter("");
                            setSubChapter("");
                            setTopic("");
                            setSubTopic("");
                          }}
                          placeholder="Enter keyword..."
                          className="w-full rounded-lg border border-gray-300 bg-white py-2 pr-16 pl-10 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        {keyword ? (
                          <button
                            type="button"
                            onClick={() => setKeyword("")}
                            className="absolute right-1.5 top-1.5 rounded bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-600 hover:bg-gray-200"
                          >
                            Clear
                          </button>
                        ) : null}
                      </div>
                    </div>
                  </div>
                  <div className="my-4 border-b border-gray-300" />
                  {(
                    [
                      {
                        label: `${guidelineType} Section`,
                        value: chapter,
                        set: (value: string) => {
                          setChapter(value);
                          setSubChapter("");
                          setTopic("");
                          setSubTopic("");
                          setKeyword("");
                        },
                        clear: () => {
                          setChapter("");
                          setSubChapter("");
                          setTopic("");
                          setSubTopic("");
                        },
                        options: chapters.data ?? [],
                        loading: chapters.isLoading,
                        placeholder: "Select Section...",
                        disabled: Boolean(keyword),
                      },
                      {
                        label: "Sub-Section",
                        value: subChapter,
                        set: (value: string) => {
                          setSubChapter(value);
                          setTopic("");
                          setSubTopic("");
                          setKeyword("");
                        },
                        clear: () => {
                          setSubChapter("");
                          setTopic("");
                          setSubTopic("");
                        },
                        options: subChapters.data ?? [],
                        loading: subChapters.isLoading,
                        placeholder: "Select Sub-Section...",
                        disabled: !chapter || Boolean(keyword),
                      },
                      {
                        label: "Topic",
                        value: topic,
                        set: (value: string) => {
                          setTopic(value);
                          setSubTopic("");
                          setKeyword("");
                        },
                        clear: () => {
                          setTopic("");
                          setSubTopic("");
                        },
                        options: topics.data ?? [],
                        loading: topics.isLoading,
                        placeholder: "Select Topic...",
                        disabled: !subChapter || Boolean(keyword),
                      },
                      {
                        label: "Sub-Topic",
                        value: subTopic,
                        set: (value: string) => {
                          setSubTopic(value);
                          setKeyword("");
                        },
                        clear: () => setSubTopic(""),
                        options: subTopics.data ?? [],
                        loading: subTopics.isLoading,
                        placeholder: "Select Sub-Topic...",
                        disabled: !topic || Boolean(keyword),
                      },
                    ] as const
                  ).map((field) => (
                    <div key={field.label}>
                      <label className="mb-1.5 block text-sm font-bold tracking-wide text-gray-700">
                        {field.label}
                      </label>
                      {field.value && !keyword ? (
                        <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                          <span className="min-w-0 flex-1 truncate text-sm text-gray-900">
                            {field.value}
                          </span>
                          <button
                            type="button"
                            aria-label="Clear selection"
                            onClick={field.clear}
                            className="shrink-0 text-gray-400 hover:text-gray-600"
                          >
                            <IconX size={16} />
                          </button>
                        </div>
                      ) : (
                        <FilterSelect
                          value={field.value}
                          onChange={field.set}
                          options={field.options}
                          placeholder={field.placeholder}
                          disabled={field.disabled}
                          isLoading={field.loading}
                        />
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      setKeyword("");
                      setYear("");
                      setChapter("");
                      setSubChapter("");
                      setTopic("");
                      setSubTopic("");
                    }}
                    className="w-full rounded-lg border border-gray-300 bg-gray-100 py-2 text-sm font-bold text-gray-700 hover:bg-gray-200"
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            </div>

            <div className="flex h-full w-full flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm lg:w-[70%]">
              <div className="flex items-center justify-between border-b border-blue-200 bg-blue-100 px-4 py-2.5">
                <h3 className="font-semibold text-blue-900">ICD-10 Results</h3>
                <div className="flex items-center gap-2">
                  <span className="rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-blue-700">
                    {icdRows.length} Result{icdRows.length !== 1 ? "s" : ""} Found
                  </span>
                  {icdRows.length > 0 ? (
                    <button
                      type="button"
                      onClick={() =>
                        allIcdExpanded
                          ? setExpandedIcd([])
                          : setExpandedIcd(icdRows.map((row) => row.id))
                      }
                      className="h-8 rounded-md border border-green-400 bg-green-200 px-3 text-sm font-semibold text-green-800"
                    >
                      {allIcdExpanded ? "Collapse All" : "Expand All"}
                    </button>
                  ) : null}
                </div>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto">
                {icdSearch.isFetching ? (
                  <Spinner label="Loading..." />
                ) : icdRows.length === 0 ? (
                  <div className="flex h-full flex-col items-center justify-center p-8">
                    <div className="mb-4 flex h-24 w-24 items-center justify-center rounded-full bg-gray-100">
                      <IconPlus size={48} className="text-gray-400" />
                    </div>
                    <p className="mb-2 text-lg font-bold text-gray-900">
                      No guidelines found
                    </p>
                    <p className="max-w-md text-center text-sm text-gray-600">
                      We couldn&apos;t find any coding guidelines matching your current
                      criteria. Try adjusting your filters or searching with a
                      different keyword.
                    </p>
                  </div>
                ) : (
                  icdRows.map((row: IcdGuideline, index: number) => {
                    const open = expandedIcd.includes(row.id);
                    const prevOpen =
                      index > 0 && expandedIcd.includes(icdRows[index - 1].id);
                    const detail =
                      icdBody.data?.find((item: IcdGuideline) => item.id === row.id) ??
                      row;
                    const codingNote = sanitizeGuidelineText(detail.codingNote);
                    return (
                      <div
                        key={row.id}
                        className={
                          open
                            ? cn(
                                "overflow-hidden border-[3px] border-solid border-fuchsia-400 bg-[#F9F0FF] shadow-sm",
                                prevOpen && "mt-2"
                              )
                            : "border-b border-solid border-gray-200"
                        }
                      >
                        <button
                          type="button"
                          onClick={() => toggleIcd(row.id)}
                          className={cn(
                            "flex w-full items-start justify-between px-4 py-3 text-left",
                            open
                              ? "border-b border-gray-300 hover:bg-fuchsia-100/50"
                              : "hover:bg-blue-50"
                          )}
                        >
                          <div className="flex-1 pr-4">
                            <h4 className="text-base font-semibold text-gray-900">
                              {formatIcdGuidelineHeader(row)}
                            </h4>
                            {row.fyear ? (
                              <p className="mt-1 text-sm text-gray-600">
                                [{row.fyear}]
                              </p>
                            ) : null}
                            {row.subTopic ? (
                              <p className="mt-1 text-sm text-gray-600">
                                {sanitizeGuidelineText(row.subTopic)}
                              </p>
                            ) : null}
                          </div>
                          <IconChevronDown
                            size={20}
                            className={cn(
                              "mt-0.5 shrink-0 text-gray-500",
                              open && "rotate-180"
                            )}
                          />
                        </button>
                        {open ? (
                          <div className="px-2 pb-2 pt-2 text-sm leading-relaxed">
                            {icdBody.isFetching && !codingNote ? (
                              <p className="px-3 py-2 text-gray-500">
                                Loading guideline note...
                              </p>
                            ) : icdBody.isError && !codingNote ? (
                              <p className="px-3 py-2 text-red-600">
                                Failed to load guideline note.
                              </p>
                            ) : !codingNote ? (
                              <p className="rounded-lg border border-slate-200 bg-white px-3 py-3 text-gray-600">
                                No guideline note is available for this
                                reference.
                              </p>
                            ) : (
                              <NoteCard title="Guideline Note:" tone="blue">
                                {codingNote}
                              </NoteCard>
                            )}
                          </div>
                        ) : null}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="absolute inset-0 flex flex-col gap-1.5 px-1 pt-1 pb-2 lg:flex-row">
            <div className="flex h-full w-full flex-col overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-sm lg:w-[30%]">
              <div className="border-b border-gray-200 bg-gray-50/50 p-3">
                <h2 className="mb-2 text-lg font-bold text-gray-900">
                  AHA Coding Clinic References:
                </h2>
                <ModeToggle
                  value={ahaFilter}
                  onChange={(value) => setAhaFilter(value as "keyword" | "year")}
                  options={[
                    { value: "keyword", label: "By Keyword" },
                    { value: "year", label: "By Year / Quarter" },
                  ]}
                />
                <div className="mt-3 space-y-3">
                  {ahaFilter === "keyword" ? (
                    <div>
                      <label className="mb-1.5 block text-sm font-semibold text-gray-900">
                        Keyword Search
                      </label>
                      <div className="relative">
                        <input
                          value={ahaKeyword}
                          onChange={(event) => {
                            setAhaKeyword(event.target.value);
                            setAhaYear("");
                            setAhaQuarters([]);
                          }}
                          placeholder="Enter keyword..."
                          className="w-full rounded-lg border border-gray-300 bg-white py-2 pr-16 pl-3 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        {ahaKeyword ? (
                          <button
                            type="button"
                            onClick={() => setAhaKeyword("")}
                            className="absolute right-1.5 top-1.5 rounded bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-600 hover:bg-gray-200"
                          >
                            Clear
                          </button>
                        ) : null}
                      </div>
                    </div>
                  ) : (
                    <>
                      <div>
                        <label className="mb-1.5 block text-sm font-semibold text-gray-900">
                          Year
                        </label>
                        <FilterSelect
                          value={ahaYear}
                          onChange={(value) => {
                            setAhaYear(value);
                            setAhaQuarters([]);
                          }}
                          placeholder="All"
                          options={(ahaYears.data ?? []).map(String)}
                          isLoading={ahaYears.isLoading}
                        />
                      </div>
                      <div>
                        <label className="mb-1.5 block text-sm font-semibold text-gray-900">
                          Quarter
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {AHA_QUARTER_CHIPS.map((chip) => {
                            const active = ahaQuarters.includes(chip.id);
                            return (
                              <button
                                key={chip.id}
                                type="button"
                                disabled={!ahaYear}
                                onClick={() =>
                                  setAhaQuarters((current) =>
                                    active
                                      ? current.filter((item) => item !== chip.id)
                                      : [...current, chip.id]
                                  )
                                }
                                className={cn(
                                  "rounded-md px-4 py-2 text-sm font-medium",
                                  active
                                    ? "bg-blue-600 text-white"
                                    : "border border-gray-300 bg-white text-black",
                                  !ahaYear && "cursor-not-allowed opacity-50"
                                )}
                              >
                                {chip.label}
                              </button>
                            );
                          })}
                        </div>
                        {!ahaYear ? (
                          <p className="mt-1 text-xs text-gray-500">
                            Select a year to choose quarters.
                          </p>
                        ) : null}
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setAhaYear("");
                          setAhaQuarters([]);
                        }}
                        className="w-full rounded-lg border border-gray-300 bg-gray-100 py-2 text-sm font-bold text-gray-700 hover:bg-gray-200"
                      >
                        Clear Filters
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="flex h-full w-full flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm lg:w-[70%]">
              <div className="flex items-center justify-between border-b border-blue-200 bg-blue-100 px-4 py-2.5">
                <h3 className="font-semibold text-blue-900">
                  AHA Coding Clinic References:
                </h3>
                <div className="flex items-center gap-2">
                  <span className="rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-blue-700">
                    {ahaRows.length} Result{ahaRows.length !== 1 ? "s" : ""} Found
                  </span>
                  {ahaRows.length > 0 ? (
                    <button
                      type="button"
                      onClick={() =>
                        allAhaExpanded
                          ? setExpandedAha([])
                          : setExpandedAha(ahaRows.map((row) => row.id))
                      }
                      className="h-8 rounded-md border border-green-400 bg-green-200 px-3 text-sm font-semibold text-green-800"
                    >
                      {allAhaExpanded ? "Collapse All" : "Expand All"}
                    </button>
                  ) : null}
                </div>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto">
                {ahaSearch.isFetching ? (
                  <Spinner label="Loading..." />
                ) : ahaRows.length === 0 ? (
                  <div className="flex h-full flex-col items-center justify-center p-8">
                    <div className="mb-4 flex h-24 w-24 items-center justify-center rounded-full bg-gray-100">
                      <IconPlus size={48} className="text-gray-400" />
                    </div>
                    <p className="mb-2 text-lg font-bold text-gray-900">
                      No guidelines found
                    </p>
                  </div>
                ) : (
                  ahaRows.map((row: AhaGuideline, index: number) => {
                    const open = expandedAha.includes(row.id);
                    const prevOpen =
                      index > 0 && expandedAha.includes(ahaRows[index - 1].id);
                    const detail =
                      ahaBody.data?.find((item: AhaGuideline) => item.id === row.id) ??
                      (open ? undefined : row);
                    const yearQuarter = formatAhaYearQuarterPage(row);
                    const informationStatement = cleanAhaMultilineText(
                      detail?.informationStatement
                    );
                    const { qaPairs, fallbackText } = detail
                      ? getAhaExpandedQaContent(detail)
                      : { qaPairs: [], fallbackText: "" };
                    return (
                      <div
                        key={row.id}
                        className={
                          open
                            ? cn(
                                "overflow-hidden border-[3px] border-solid border-fuchsia-400 bg-[#F9F0FF] shadow-sm",
                                prevOpen && "mt-2"
                              )
                            : "border-b border-solid border-gray-200"
                        }
                      >
                        <button
                          type="button"
                          onClick={() => toggleAha(row.id)}
                          className={cn(
                            "flex w-full items-start justify-between px-4 py-3 text-left",
                            open
                              ? "border-b border-gray-300 hover:bg-fuchsia-100/50"
                              : "hover:bg-blue-50"
                          )}
                        >
                          <div className="flex-1 pr-4">
                            <h4 className="text-base font-semibold text-gray-900">
                              {formatAhaGuidelineTitle(row)}
                            </h4>
                            {yearQuarter ? (
                              <p className="mt-1 text-sm text-gray-600">
                                {yearQuarter}
                              </p>
                            ) : null}
                          </div>
                          <IconChevronDown
                            size={20}
                            className={cn(
                              "mt-0.5 shrink-0 text-gray-500",
                              open && "rotate-180"
                            )}
                          />
                        </button>
                        {open ? (
                          <div className="space-y-3 px-2 pb-2 pt-2 text-sm leading-relaxed">
                            {ahaBody.isFetching && !detail ? (
                              <p className="px-3 py-2 text-gray-500">
                                Loading reference details...
                              </p>
                            ) : ahaBody.isError && !detail ? (
                              <p className="px-3 py-2 text-red-600">
                                Failed to load reference details.
                              </p>
                            ) : (
                              <>
                                {informationStatement ? (
                                  <NoteCard title="" tone="white">
                                    {informationStatement}
                                  </NoteCard>
                                ) : null}
                                {qaPairs.map((pair, pairIndex) => {
                                  const pairSuffix =
                                    qaPairs.length > 1 ? ` ${pairIndex + 1}` : "";
                                  return (
                                    <div
                                      key={`${row.id}-qa-${pairIndex}`}
                                      className="space-y-3"
                                    >
                                      {pair.question ? (
                                        <NoteCard
                                          title={`Question${pairSuffix}:`}
                                          tone="red"
                                          icon={<IconHelpCircle size={16} className="text-black" />}
                                        >
                                          {pair.question}
                                        </NoteCard>
                                      ) : null}
                                      {pair.answer ? (
                                        <NoteCard
                                          title={`Answer${pairSuffix}:`}
                                          tone="green"
                                          icon={
                                            <IconCircleCheck size={16} className="text-black" />
                                          }
                                        >
                                          {pair.answer}
                                        </NoteCard>
                                      ) : null}
                                    </div>
                                  );
                                })}
                                {fallbackText ? (
                                  <NoteCard title="" tone="white">
                                    {fallbackText}
                                  </NoteCard>
                                ) : null}
                                {!informationStatement &&
                                qaPairs.length === 0 &&
                                !fallbackText ? (
                                  <p className="rounded-lg border border-slate-200 bg-white px-3 py-3 text-gray-600">
                                    No additional detail is available for this
                                    reference.
                                  </p>
                                ) : null}
                              </>
                            )}
                          </div>
                        ) : null}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CodingGuideTab;
